import { ulid } from "ulid";
import { dispatchEvent } from "./dispatchEvent";
import { pageConfigContentId } from "./globalConfig";
// This module implements a very simple event dispatch system.
// It will try to use a persistent deviceId for a given user's browser.
// The general schema of the events is common data at the top level, and event-specific
// data nested under a key named after the event.
// In the current iteration, article view and promo events are blocked if they are
// coming from a draft editor preview. Potentially a future version should instead
// set an attribute (like "location: draft") instead, but while this is an experiment
// it's better to keep things simple.
export function trackArticleViewed(contentId) {
    if (pageIsDraft())
        return;
    const event = {
        eventType: "articleViewed",
        deviceId: findOrCreateDeviceId(),
        articleViewed: {
            contentId: contentId.toString(),
            deviceType: deviceType(),
        },
    };
    dispatchEvent(event);
}
// The caller of the promo tracking functions may not actually have the contentId,
// so we fetch that independently if it's missing.
export function trackPromoViewed(promoId, promoType, contentId = findContentId()) {
    if (pageIsDraft())
        return;
    const event = {
        eventType: "promoViewed",
        deviceId: findOrCreateDeviceId(),
        promoViewed: {
            contentId: contentId.toString(),
            promoId: promoId.toString(),
            promoType: promoType,
            deviceType: deviceType(),
        },
    };
    dispatchEvent(event);
}
// The caller of the promo tracking functions may not actually have the contentId,
// so we fetch that independently if it's missing.
export function trackPromoEngaged(promoId, promoType, contentId = findContentId()) {
    if (pageIsDraft())
        return;
    const event = {
        eventType: "promoEngaged",
        deviceId: findOrCreateDeviceId(),
        promoEngaged: {
            contentId: contentId.toString(),
            promoId: promoId.toString(),
            promoType: promoType,
            deviceType: deviceType(),
        },
    };
    dispatchEvent(event);
}
// Try to locate the current page's content id by inspecting the DOM.
// There are a couple of ways to try and find the content id, so try both.
function findContentId() {
    const globalPageContentId = pageConfigContentId();
    if (globalPageContentId) {
        return globalPageContentId;
    }
    const element = document.querySelector("link[rel='canonical']");
    if (element && element instanceof HTMLLinkElement) {
        return element.href.match(/-(\d{3,9})$/)?.[1] || "";
    }
    else {
        return "";
    }
}
// Try to determine if the current page is a preview in the draft editor.
function pageIsDraft() {
    try {
        const knownDraftCssClasses = ["drafts", "drafts-edit"];
        if (knownDraftCssClasses.find((c) => document.body.classList.contains(c))) {
            return true;
        }
        return window.location.pathname.startsWith("/drafts/");
    }
    catch {
        return false;
    }
}
// Guess if the user's device is a mobile or desktop.
// 1024x600 is probably the smallest screen size we consider a desktop (eg. 10" netbooks)
// Mobile phones report virtual "CSS Pixels", so even retina screen iphones will report relatively
// small screen sizes. For eg iphone 15 pro max is 430 × 932.
// When checking the size, remember that a phone might be in landscape mode, so check height and width.
function deviceType() {
    try {
        if (window.screen.width >= 600 && window.screen.height >= 600)
            return "desktop";
        else
            return "mobile";
    }
    catch {
        return "unknown";
    }
}
function findOrCreateDeviceId() {
    const deviceIdKey = "tcDeviceId";
    try {
        let deviceId = window.localStorage.getItem(deviceIdKey);
        if (deviceId == undefined || deviceId.length < 26) {
            deviceId = "v1:" + ulid();
            window.localStorage.setItem(deviceIdKey, deviceId);
        }
        return deviceId;
    }
    catch {
        // User's browser probably isn't allowing storage, which is unusual for a first-party domain.
        // Whatever, generate a temporary code for now. Note the different prefix.
        return "x1:" + ulid();
    }
}
